import { DialogModal } from "@/components/DialogModal";

import { ApiUser } from "@/types";

interface ToggleStatusModalProps {
  visible: boolean;
  user: ApiUser | null;
  isPending: boolean;
  /**
   * Whether the user being deactivated is currently an approved co-signer.
   * When `true`, the modal surfaces the amber "Remove as a co-signer
   * separately" callout because their signing key is still part of the
   * quorum after deactivation. Pending / invited co-signers do not get the
   * callout — they have not (yet) contributed a key to the quorum.
   */
  isApprovedCoSigner?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

const getUserNameText = (firstName?: string, lastName?: string) => {
  const parts = [firstName, lastName].filter((p): p is string => Boolean(p));
  return parts.length ? parts.join(" ") : "this user";
};

const getFirstNameText = (firstName?: string, lastName?: string) =>
  firstName || lastName || "This user";

export const ToggleStatusModal = ({
  visible,
  user,
  isPending,
  isApprovedCoSigner = false,
  onConfirm,
  onClose,
}: ToggleStatusModalProps) => {
  const isActive = Boolean(user?.is_active);
  const fullName = getUserNameText(user?.first_name, user?.last_name);
  const firstName = getFirstNameText(user?.first_name, user?.last_name);

  const title = isActive ? `Deactivate ${fullName}?` : `Activate ${fullName}?`;
  const bodyText = isActive
    ? "They'll lose access to VIA Disbursement Platform. Their account and history stay on record, and you can reactivate them later."
    : `Please confirm account activation for ${fullName}.`;

  const confirmLabel = isActive ? "Deactivate account" : "Confirm";
  const showCoSignerCallout = isActive && isApprovedCoSigner;

  return (
    <DialogModal
      visible={visible}
      onClose={onClose}
      title={title}
      width="50"
      footerAlignment="stretch"
      primaryAction={{
        label: "Cancel",
        variant: "tertiary",
        onClick: onClose,
        isLoading: isPending,
      }}
      secondaryAction={{
        label: confirmLabel,
        variant: isActive ? "destructive" : "primary",
        onClick: onConfirm,
        isLoading: isPending,
      }}
    >
      <div className="ToggleStatusModal">
        <p className="ToggleStatusModal__body">{bodyText}</p>

        {showCoSignerCallout ? (
          <div
            className="ToggleStatusModal__coSignerCallout"
            role="note"
            aria-label="Co-signer reminder"
          >
            <div className="ToggleStatusModal__coSignerCalloutTitle">
              Remove as a co-signer separately
            </div>
            <div className="ToggleStatusModal__coSignerCalloutBody">
              Deactivating won&apos;t remove {firstName} as a co-signer. That&apos;s a separate
              step, and it needs Owner approval.
            </div>
          </div>
        ) : null}
      </div>
    </DialogModal>
  );
};
